import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { gameSessionsAPI } from '../lib/supabase';
import CombatGame from './CombatGame';
import JumpGame from './JumpGame';
import MirrorGame from './MirrorGame';
import { Trophy, TrendingUp, Target, Home } from 'lucide-react';

const GameWrapper = () => {
  const { gameType } = useParams();
  const navigate = useNavigate();
  const [gameEnded, setGameEnded] = useState(false);
  const [sessionData, setSessionData] = useState(null);

  const handleGameEnd = async (data) => {
    setSessionData(data);
    setGameEnded(true);

    try {
      const playerName = localStorage.getItem('player_name') || 'Anonymous';
      await gameSessionsAPI.create({
        ...data,
        player_name: playerName
      });
      console.log('Game session saved to Supabase');
    } catch (error) {
      console.error('Failed to save session:', error);
    }
  };

  const renderGame = () => {
    switch (gameType) {
      case 'combat':
        return <CombatGame onGameEnd={handleGameEnd} />;
      case 'jump':
        return <JumpGame onGameEnd={handleGameEnd} />;
      case 'mirror':
        return <MirrorGame onGameEnd={handleGameEnd} />;
      default:
        navigate('/');
        return null;
    }
  };

  if (gameEnded && sessionData) {
    return (
      <div className="min-h-screen w-full bg-black flex items-center justify-center p-6">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="bg-black/80 backdrop-blur-xl border border-primary/50 rounded-2xl p-8 md:p-12 max-w-2xl w-full shadow-neon-blue"
          data-testid="game-end-screen"
        >
          <div className="text-center mb-8">
            <motion.div
              initial={{ y: -20 }}
              animate={{ y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <Trophy className="w-20 h-20 text-accent mx-auto mb-4 animate-pulse-neon" />
            </motion.div>
            <h2 className="font-heading text-4xl md:text-5xl font-black uppercase tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent mb-2">
              GAME OVER!
            </h2>
            <p className="text-muted-foreground font-body uppercase tracking-wider">Great workout session!</p>
          </div>

          <div className="space-y-4 mb-8">
            <motion.div
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="bg-muted/20 rounded-lg p-4 flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                <Trophy className="w-6 h-6 text-accent" />
                <span className="font-heading text-lg uppercase tracking-wide text-muted-foreground">Final Score</span>
              </div>
              <span className="font-heading text-3xl font-black text-accent">{sessionData.score}</span>
            </motion.div>

            <motion.div
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="bg-muted/20 rounded-lg p-4 flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                <Target className="w-6 h-6 text-primary" />
                <span className="font-heading text-lg uppercase tracking-wide text-muted-foreground">Accuracy</span>
              </div>
              <span className="font-heading text-3xl font-black text-primary">{sessionData.accuracy.toFixed(0)}%</span>
            </motion.div>

            <motion.div
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="bg-muted/20 rounded-lg p-4 flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                <TrendingUp className="w-6 h-6 text-secondary" />
                <span className="font-heading text-lg uppercase tracking-wide text-muted-foreground">Duration</span>
              </div>
              <span className="font-heading text-2xl font-black text-secondary">{sessionData.duration}s</span>
            </motion.div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <motion.button
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/')}
              className="flex items-center justify-center gap-2 bg-black/60 border border-primary/50 rounded-lg px-6 py-4 font-heading font-bold uppercase text-primary hover:bg-primary/10 transition-colors"
              data-testid="home-button"
            >
              <Home className="w-5 h-5" />
              Home
            </motion.button>

            <motion.button
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.7 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => window.location.reload()}
              className="bg-gradient-to-r from-primary to-accent rounded-lg px-6 py-4 font-heading font-bold uppercase text-black shadow-neon-blue hover:shadow-neon-green transition-all"
              data-testid="play-again-button"
            >
              Play Again
            </motion.button>
          </div>
        </motion.div>
      </div>
    );
  }

  return renderGame();
};

export default GameWrapper;
